import React, { useState, useCallback, useEffect, useRef } from 'react';

import { Popper, type PopperProps } from '@mui/material';

import { Paper, SwipeableMobileBottomDrawer } from '@liscio/ui';

import { ContactCard } from './ContactCard';
import { useFirmViewMode } from 'custom-hooks/useFirmViewMode';

const CARD_POPPER_OPTIONS: PopperProps['popperOptions'] = { strategy: 'fixed' };

const CARD_MODIFIERS: PopperProps['modifiers'] = [
  {
    name: 'flip',
    options: {
      // Allow opening below when the card is wider than the room; preventOverflow slides it back.
      altAxis: false,
      fallbackPlacements: ['bottom-start', 'left', 'top-start'],
    },
  },
  { name: 'preventOverflow', options: { padding: 8 } },
  { name: 'offset', options: { offset: [0, 8] } },
];

export interface ContactCardPopoverProps {
  contactId: number | string;
  children: React.ReactElement;
  showNavigationLinks?: boolean;
}

export const ContactCardPopover: React.FC<ContactCardPopoverProps> = ({
  contactId,
  children,
  showNavigationLinks = true,
}) => {
  const { isMobile } = useFirmViewMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  }, []);

  const handleClick = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      cancelScheduledClose();
      setAnchorEl(event.currentTarget);
      setIsOpen(true);
    },
    [cancelScheduledClose]
  );

  const handleClose = useCallback(() => {
    cancelScheduledClose();
    setIsOpen(false);
    setAnchorEl(null);
  }, [cancelScheduledClose]);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(handleClose, 100);
  }, [cancelScheduledClose, handleClose]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  const popperRef = useRef<{ update: () => void } | null>(null);
  const setPopper = useCallback((popper: { update: () => void } | null) => {
    popperRef.current = popper;
  }, []);

  const resizeObserverRef = useRef<ResizeObserver>();
  const observeCardSize = useCallback((card: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = undefined;
    if (!card) return;
    resizeObserverRef.current = new ResizeObserver(() =>
      popperRef.current?.update()
    );
    resizeObserverRef.current.observe(card);
  }, []);

  const handleEscape = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Escape' || !isOpen) return;
      event.stopPropagation();
      handleClose();
    },
    [isOpen, handleClose]
  );

  // Clone children to add click handler
  const trigger = React.cloneElement(children, {
    onClick: handleClick,
    onMouseEnter: !isMobile ? handleClick : undefined,
    onMouseLeave: !isMobile ? scheduleClose : undefined,
    onFocus: !isMobile ? handleClick : undefined,
    onKeyDown: !isMobile ? handleEscape : undefined,
    style: { ...children.props.style, cursor: 'pointer' },
  });

  if (isMobile) {
    return (
      <>
        {trigger}
        {isOpen && (
          <SwipeableMobileBottomDrawer
            open={isOpen}
            onClose={handleClose}
            containerProps={{ sx: { pt: 3 } }}
          >
            <ContactCard
              contactId={contactId}
              fullWidth
              onClose={handleClose}
              showNavigationLinks={showNavigationLinks}
            />
          </SwipeableMobileBottomDrawer>
        )}
      </>
    );
  }

  return (
    <>
      {trigger}
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        container={anchorEl?.closest('[role="dialog"]')}
        placement="right"
        popperOptions={CARD_POPPER_OPTIONS}
        popperRef={setPopper}
        modifiers={CARD_MODIFIERS}
        sx={{ zIndex: 'tooltip' }}
        className="contact-card-popper"
      >
        <Paper
          ref={observeCardSize}
          elevation={8}
          sx={{ minWidth: 280, p: 2 }}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={handleClose}
          onKeyDown={handleEscape}
        >
          <ContactCard
            contactId={contactId}
            showNavigationLinks={showNavigationLinks}
          />
        </Paper>
      </Popper>
    </>
  );
};

export default ContactCardPopover;
